import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { brandColors } from "../../App";
import { clientInvoice } from "../../components/Pdf/clientInvoice";
import { ClientTransferList } from "../../services/inventoryService";
import { RootState } from "../../store";

type OrderStatusFilter = "ALL" | "PENDING" | "APPROVED";

interface TransferItem {
  qty_packed: string | number;
  qty_unpacked: string | number | null;
  status: string;
  created_at: string;
  sending_store: string;
  receiving_store: string;
  product_id: string;
  invoice_no: string;
  store: string;
  unit: string;
  packed: string | number;
  msrp?: string | number | null;
  hst?: string | number | null;
  transfer_id: string;
  product: string;
  notes: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface OrderGroup {
  email: string;
  invoice_no: string;
  name: string;
  notes: string;
  created_at: string;
  status: string;
  transfers: TransferItem[];
}

interface PaginationState {
  page: number;
  limit: number;
  totalPages: number;
}

const PAGE_SIZE_FALLBACK = 10;

const pageBackground = "#F7F9F7";
const borderColor = "#E3E8E3";
const mutedText = "#68746B";
const lightGreen = "#F1F8F2";

/**
 * Converts the flat transfer response into one object per invoice.
 *
 * ClientTransferList currently returns product rows, so grouping here allows
 * the page to behave as an order-history master/detail interface.
 */
function aggregateByInvoice(transfers: TransferItem[]): OrderGroup[] {
  const orderMap = new Map<string, OrderGroup>();

  transfers.forEach((item) => {
    const existingOrder = orderMap.get(item.invoice_no);

    if (!existingOrder) {
      orderMap.set(item.invoice_no, {
        email: item.email,
        invoice_no: item.invoice_no,
        name: `${item.first_name ?? ""} ${item.last_name ?? ""}`.trim(),
        notes: item.notes ?? "",
        created_at: item.created_at,
        status: item.status,
        transfers: [item],
      });

      return;
    }

    existingOrder.transfers.push(item);

    /*
     * If any product in the order is still pending, represent the
     * complete invoice as pending.
     */
    if (item.status === "PENDING") {
      existingOrder.status = "PENDING";
    }
  });

  return Array.from(orderMap.values());
}

function safeNumber(value: string | number | null | undefined): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatPercent(value: number): string {
  return new Intl.NumberFormat("en-CA", {
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Product HST is stored as a percentage in the product record. Supporting a
 * decimal value as well keeps the calculation safe if older data contains 0.13.
 */
function normalizeTaxRate(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === "") return 0;

  const parsed =
    typeof value === "string"
      ? Number(value.replace("%", "").trim())
      : Number(value);

  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return parsed > 1 ? parsed / 100 : parsed;
}

function formatDate(value?: string): string {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value.split("T")[0] || value;
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function formatTime(value?: string): string {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function getStatusLabel(status?: string): string {
  switch (status?.toUpperCase()) {
    case "APPROVED":
      return "PREPARED";

    case "PENDING":
      return "PENDING";

    default:
      return status?.toUpperCase() || "UNKNOWN";
  }
}

function StatusChip({ status }: { status?: string }) {
  const normalizedStatus = status?.toUpperCase();

  if (normalizedStatus === "APPROVED") {
    return (
      <Chip
        label="PREPARED"
        size="small"
        sx={{
          height: 24,
          borderRadius: "6px",
          backgroundColor: "#E2F5E7",
          color: "#08752C",
          fontWeight: 700,
          fontSize: "0.7rem",
        }}
      />
    );
  }

  if (normalizedStatus === "PENDING") {
    return (
      <Chip
        label="PENDING"
        size="small"
        sx={{
          height: 24,
          borderRadius: "6px",
          backgroundColor: "#FFF2DD",
          color: "#A35A00",
          fontWeight: 700,
          fontSize: "0.7rem",
        }}
      />
    );
  }

  return (
    <Chip
      label={getStatusLabel(status)}
      size="small"
      sx={{
        height: 24,
        borderRadius: "6px",
        backgroundColor: "#EFF2F0",
        color: "#526057",
        fontWeight: 700,
        fontSize: "0.7rem",
      }}
    />
  );
}

interface PricingSummaryProps {
  subtotal: number;
  discountPercent: number;
  discountAmount: number;
  tax: number;
  total: number;
  compact?: boolean;
}

/**
 * Shared financial summary used by desktop and the mobile invoice modal.
 * Tax is calculated after discount, matching the amount the client pays.
 */
function PricingSummary({
  subtotal,
  discountPercent,
  discountAmount,
  tax,
  total,
  compact = false,
}: PricingSummaryProps) {
  const rows = [
    { label: "Subtotal", value: formatCurrency(subtotal) },
    ...(discountPercent > 0
      ? [
          {
            label: `Discount (${formatPercent(discountPercent)}%)`,
            value: `-${formatCurrency(discountAmount)}`,
          },
        ]
      : []),
    { label: "Tax (HST)", value: formatCurrency(tax) },
  ];

  return (
    <Box
      sx={{
        px: compact ? 1.5 : 2,
        py: compact ? 1.1 : 1.35,
        backgroundColor: "#FBFCFB",
        borderTop: `1px solid ${borderColor}`,
        flexShrink: 0,
      }}
    >
      <Stack spacing={compact ? 0.35 : 0.45}>
        {rows.map((row) => (
          <Stack
            key={row.label}
            direction="row"
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography variant="caption" sx={{ color: mutedText }}>
              {row.label}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "#344138", whiteSpace: "nowrap" }}
            >
              {row.value}
            </Typography>
          </Stack>
        ))}

        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            pt: compact ? 0.45 : 0.6,
            mt: compact ? 0.15 : 0.25,
            borderTop: `1px solid ${borderColor}`,
          }}
        >
          <Typography
            variant={compact ? "body2" : "subtitle2"}
            sx={{ fontWeight: 700, color: "#18221B" }}
          >
            Total
          </Typography>
          <Typography
            variant={compact ? "subtitle1" : "h6"}
            sx={{
              fontWeight: 800,
              color: brandColors.header,
              whiteSpace: "nowrap",
            }}
          >
            {formatCurrency(total)}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

const ClientOrderRequests: React.FC = () => {
  const authUser = useSelector((state: RootState) => state.auth.user);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  const [orders, setOrders] = useState<OrderGroup[]>([]);
  const [selectedInvoiceNo, setSelectedInvoiceNo] = useState<string | null>(
    null,
  );

  const [statusFilter, setStatusFilter] = useState<OrderStatusFilter>("ALL");

  const [searchTerm, setSearchTerm] = useState("");

  const [page, setPage] = useState(1);

  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    limit: PAGE_SIZE_FALLBACK,
    totalPages: 1,
  });

  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);

  /**
   * Load one API page of client orders.
   *
   * IMPORTANT:
   * "ALL" intentionally omits status from the API request. This assumes that
   * ClientTransferList/backend treats a missing status as "return all".
   */
  useEffect(() => {
    let cancelled = false;

    const loadOrders = async () => {
      setLoading(true);

      try {
        const requestFilter =
          statusFilter === "ALL"
            ? {status: 'ALL'}
            : {
                status: statusFilter,
              };

        const clientData = await ClientTransferList(requestFilter, page);

        if (cancelled) return;

        const aggregatedOrders = aggregateByInvoice(clientData.data ?? []);

        setOrders(aggregatedOrders);

        setPagination({
          page: clientData.pagination?.page ?? page,
          limit: clientData.pagination?.limit ?? PAGE_SIZE_FALLBACK,
          totalPages: clientData.pagination?.totalPages ?? 1,
        });

        /*
         * Preserve the selected invoice only while it still exists.
         * Desktop auto-selection is handled separately so mobile can
         * initially render as a clean invoice-only list.
         */
        setSelectedInvoiceNo((currentInvoiceNo) => {
          const currentStillExists = aggregatedOrders.some(
            (order) => order.invoice_no === currentInvoiceNo,
          );
          return currentStillExists ? currentInvoiceNo : null;
        });
      } catch (error) {
        if (!cancelled) {
          console.error("Error loading client order history:", error);
          setOrders([]);
          setSelectedInvoiceNo(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadOrders();

    return () => {
      cancelled = true;
    };
  }, [page, statusFilter]);

  /**
   * Desktop keeps the approved master/detail behavior by selecting the
   * first invoice. Mobile starts collapsed and expands only after a tap.
   */
  useEffect(() => {
    if (isDesktop) {
      setSelectedInvoiceNo(
        (currentInvoiceNo) => currentInvoiceNo ?? orders[0]?.invoice_no ?? null,
      );
      return;
    }

    setSelectedInvoiceNo(null);
  }, [isDesktop, orders]);

  /**
   * Current backend contract shown in the existing component only proves
   * status + page filtering. Therefore search is intentionally performed
   * against the currently loaded page.
   *
   * Once the backend exposes server-side search, move this value into the
   * ClientTransferList request.
   */
  const visibleOrders = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    if (!query) {
      return orders;
    }

    return orders.filter((order) => {
      const searchableText = [
        order.invoice_no,
        order.notes,
        formatDate(order.created_at),
        getStatusLabel(order.status),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(query);
    });
  }, [orders, searchTerm]);

  const selectedOrder = useMemo(
    () =>
      orders.find((order) => order.invoice_no === selectedInvoiceNo) ?? null,
    [orders, selectedInvoiceNo],
  );

  /*
   * Name/email are common across the client's invoices, so show them once
   * instead of repeating them in every order row.
   */
  const clientIdentity = orders[0] ?? selectedOrder;

  const totalQuantity = useMemo(() => {
    if (!selectedOrder) return 0;

    return selectedOrder.transfers.reduce(
      (total, item) => total + safeNumber(item.qty_packed),
      0,
    );
  }, [selectedOrder]);

  const totalUnpackedQuantity = useMemo(() => {
    if (!selectedOrder) return 0;

    return selectedOrder.transfers.reduce(
      (total, item) => total + safeNumber(item.qty_unpacked),
      0,
    );
  }, [selectedOrder]);

  const discountPercent = Math.min(
    100,
    Math.max(0, safeNumber(authUser?.discount_percent)),
  );

  /**
   * Pricing order:
   * 1. MSRP x requested pack quantity = subtotal.
   * 2. Apply the client's discount to merchandise.
   * 3. Apply each product's HST rate to its discounted line amount.
   * 4. Add tax to the discounted merchandise total.
   */
  const pricing = useMemo(() => {
    if (!selectedOrder) {
      return { subtotal: 0, discountAmount: 0, tax: 0, total: 0 };
    }

    const discountFactor = 1 - discountPercent / 100;
    const subtotal = selectedOrder.transfers.reduce(
      (sum, item) => sum + safeNumber(item.msrp) * safeNumber(item.qty_packed),
      0,
    );
    const discountAmount = subtotal * (discountPercent / 100);
    const discountedSubtotal = subtotal - discountAmount;
    const tax = selectedOrder.transfers.reduce((sum, item) => {
      const discountedLineAmount =
        safeNumber(item.msrp) * safeNumber(item.qty_packed) * discountFactor;
      return sum + discountedLineAmount * normalizeTaxRate(item.hst);
    }, 0);

    return {
      subtotal,
      discountAmount,
      tax,
      total: discountedSubtotal + tax,
    };
  }, [discountPercent, selectedOrder]);

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleStatusChange = (event: SelectChangeEvent<OrderStatusFilter>) => {
    setStatusFilter(event.target.value as OrderStatusFilter);
    setPage(1);
    setSearchTerm("");
  };

  interface OrderMetricProps {
    label: string;
    value: React.ReactNode;
  }

  const CompactMetric: React.FC<OrderMetricProps> = ({ label, value }) => {
    return (
      <Box
        sx={{
          px: 2,
          py: 0,
          // minHeight: 28,

          display: "flex",
          flexDirection: "column",
          justifyContent: "center",

          borderRight: {
            xs: "none",
            sm: `1px solid ${borderColor}`,
          },

          borderBottom: {
            xs: `1px solid ${borderColor}`,
            lg: "none",
          },

          backgroundColor: "#FFFFFF",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: mutedText,
            mb: 0.35,
          }}
        >
          {label}
        </Typography>

        <Typography
          variant="h6"
          sx={{
            color: "#172019",
            fontWeight: 700,
            lineHeight: 1.25,
          }}
        >
          {value}
        </Typography>
      </Box>
    );
  };
  /**
   * Uses the same invoice generator already used by the existing page.
   * A separate Download action should only be added if clientInvoice exposes
   * a distinct download/save behavior.
   */
  const handlePrintInvoice = async (
    order: OrderGroup | null = selectedOrder,
  ) => {
    if (!order || printing) return;

    setPrinting(true);

    try {
      await clientInvoice(
        order.transfers,
        authUser?.discount_percent || 0,
        order.notes,
      );
    } catch (error) {
      console.error("Error generating client invoice:", error);
      alert("Something went wrong while generating the invoice.");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        backgroundColor: pageBackground,
        p: {
          xs: 1.5,
          md: 2.5,
        },
      }}
    >
      {/* Page heading */}
      <Stack
        direction={{
          xs: "column",
          sm: "row",
        }}
        spacing={1}
        sx={{
          display: { xs: "none", lg: "flex" },
          mb: 2,
          alignItems: {
            xs: "flex-start",
            sm: "center",
          },
          justifyContent: "space-between",
        }}
      >
        {/* <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#18221B",
            }}
          >
            Orders
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: mutedText,
              mt: 0.25,
            }}
          >
            View and track all your order history.
          </Typography>
        </Box> */}
        {/* Client information */}
        {/* <Paper
          elevation={0}
          sx={{
            mb: 2,
            border: `1px solid ${borderColor}`,
            flexDirection: "row",
            borderRadius: 2,
            display: "flex",
            flexGrow: 1,
            px: {
              xs: 2,
              md: 2.5,
            },
            py: 2,
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                backgroundColor: "#E2F5E7",
                color: brandColors.header,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <PersonOutlineRoundedIcon />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  color: "#1D271F",
                }}
              >
                {clientIdentity?.name || "Client"}
              </Typography>

              <Typography
                variant="body2"
                noWrap
                sx={{
                  color: mutedText,
                }}
              >
                {clientIdentity?.email || "—"}
              </Typography>
            </Box>
          </Stack>
          {selectedOrder && (
            <Box sx={{ minWidth: 0 }}>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: "#18221B",
                  }}
                >
                  Invoice #{selectedOrder.invoice_no}
                </Typography>

                <StatusChip status={selectedOrder.status} />
              </Stack>

              <Typography
                variant="body2"
                sx={{
                  color: mutedText,
                  mt: 0.5,
                }}
              >
                {formatDate(selectedOrder.created_at)}
                {" • "}
                {formatTime(selectedOrder.created_at)}
              </Typography>
            </Box>
          )}
          <Button
            variant="outlined"
            startIcon={
              printing ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <PrintOutlinedIcon />
              )
            }
            disabled={printing}
            onClick={() => {
              void handlePrintInvoice();
            }}
            sx={{
              color: "#1E2A21",
              borderColor: "#CDD5CF",
              textTransform: "none",
              fontWeight: 600,
              flexShrink: 0,
              "&:hover": {
                borderColor: brandColors.header,
                backgroundColor: "#F7FBF7",
              },
            }}
          >
            {printing ? "Preparing..." : "Print"}
          </Button>
          {selectedOrder && (
            <Paper
              // variant="outlined"
              sx={{
                borderColor,
                borderRadius: 2,
                boxShadow: "none",
                overflow: "hidden",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  px: 2,
                  pt: 1.5,
                  pb: 0.5,
                }}
              >
                Order Summary
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(4, minmax(0, 1fr))",
                  },
                }}
              >
                <SummaryItem
                  label="Total Items"
                  value={selectedOrder.transfers.length}
                />

                <SummaryItem label="Total Quantity" value={totalQuantity} />

                <SummaryItem label="Unpacked" value={totalUnpackedQuantity} />

                <SummaryItem
                  label="Order Date"
                  value={formatDate(selectedOrder.created_at)}
                  hideBorder
                />
              </Box>
            </Paper>
          )}
          {selectedOrder && (
            <Stack
              direction="row"
              spacing={0.75}
              sx={{
                mt: 1.25,
                alignItems: "flex-start",
              }}
            >
              <NotesOutlinedIcon
                sx={{
                  fontSize: 18,
                  color: mutedText,
                  mt: 0.1,
                }}
              />

              <Typography
                variant="body2"
                sx={{
                  color: mutedText,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontWeight: 600,
                    color: "#39463D",
                  }}
                >
                  Order notes:
                </Box>{" "}
                {selectedOrder.notes?.trim() || "—"}
              </Typography>
            </Stack>
          )}
        </Paper> */}
        {/* =========================================================
    SELECTED ORDER OVERVIEW - SINGLE DESKTOP ROW
========================================================== */}
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            border: `1px solid ${borderColor}`,
            borderRadius: 2,
            backgroundColor: "#FFFFFF",
            overflow: "hidden",
            flexGrow: 1,
          }}
        >
          {selectedOrder && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  lg: "minmax(190px, 1.1fr) minmax(255px, 1.4fr) 95px 115px 95px minmax(220px, 1.3fr) auto",
                },
                alignItems: "center",
                // minHeight: 96,
              }}
            >
              {/* CLIENT */}
              <Stack
                direction="row"
                spacing={1.25}
                sx={{
                  alignItems: "center",
                  minWidth: 0,
                  px: 2,
                  py: 1.5,
                  borderRight: {
                    lg: `1px solid ${borderColor}`,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 42,
                    // height: 42,
                    borderRadius: "50%",
                    backgroundColor: "#E7F6EA",
                    color: brandColors.header,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <PersonOutlineRoundedIcon sx={{ fontSize: 22 }} />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="subtitle2"
                    noWrap
                    sx={{
                      fontWeight: 700,
                      color: "#18221B",
                    }}
                  >
                    {clientIdentity?.name || "Client"}
                  </Typography>

                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      display: "block",
                      color: mutedText,
                      mt: 0.2,
                    }}
                  >
                    {clientIdentity?.email || "—"}
                  </Typography>
                </Box>
              </Stack>

              {/* INVOICE */}
              <Box
                sx={{
                  minWidth: 0,
                  px: 2,
                  py: 1.5,
                  borderRight: {
                    lg: `1px solid ${borderColor}`,
                  },
                }}
              >
                <Stack
                  direction="row"
                  spacing={0.8}
                  sx={{
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    // variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#18221B",
                      lineHeight: 1,
                    }}
                  >
                    Invoice #{selectedOrder.invoice_no}
                  </Typography>

                  <StatusChip status={selectedOrder.status} />
                </Stack>

                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: mutedText,
                    mt: 0.5,
                  }}
                >
                  {formatDate(selectedOrder.created_at)}
                  {" • "}
                  {formatTime(selectedOrder.created_at)}
                </Typography>
              </Box>

              {/* TOTAL ITEMS */}
              <CompactMetric
                label="Items"
                value={selectedOrder.transfers.length}
              />

              {/* TOTAL QUANTITY */}
              <CompactMetric label="Quantity" value={totalQuantity} />

              {/* UNPACKED */}
              <CompactMetric label="Unpacked" value={totalUnpackedQuantity} />

              {/* NOTES */}
              <Box
                sx={{
                  minWidth: 0,
                  px: 2,
                  py: 1.5,
                  borderRight: {
                    lg: `1px solid ${borderColor}`,
                  },
                }}
              >
                <Stack
                  direction="row"
                  spacing={0.6}
                  sx={{
                    alignItems: "center",
                    mb: 0.4,
                  }}
                >
                  <NotesOutlinedIcon
                    sx={{
                      fontSize: 16,
                      color: brandColors.header,
                    }}
                  />

                  <Typography
                    variant="caption"
                    sx={{
                      color: mutedText,
                      fontWeight: 600,
                    }}
                  >
                    Order Notes
                  </Typography>
                </Stack>

                <Typography
                  variant="body2"
                  title={selectedOrder.notes || ""}
                  sx={{
                    color: selectedOrder.notes?.trim() ? "#28342B" : mutedText,
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {selectedOrder.notes?.trim() || "No notes"}
                </Typography>
              </Box>

              {/* PRINT */}
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={
                    printing ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <PrintOutlinedIcon />
                    )
                  }
                  disabled={printing}
                  onClick={() => {
                    void handlePrintInvoice();
                  }}
                  sx={{
                    minWidth: 100,
                    minHeight: 40,
                    px: 2,
                    color: "#263128",
                    borderColor: "#D4DBD5",
                    borderRadius: 1.5,
                    textTransform: "none",
                    fontWeight: 600,
                    whiteSpace: "nowrap",

                    "&:hover": {
                      borderColor: brandColors.header,
                      backgroundColor: "#F5FAF6",
                    },
                  }}
                >
                  {printing ? "Preparing..." : "Print"}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Stack>

      {/* Responsive master/detail layout */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "minmax(330px, 4fr) minmax(0, 8fr)",
          },
          gap: { xs: 1.5, lg: 2 },
          alignItems: "stretch",
          minWidth: 0,
        }}
      >
        {/* LEFT: invoice list. On desktop only the middle list area scrolls. */}
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${borderColor}`,
            borderRadius: 2,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            height: {
              xs: "auto",
              lg: "clamp(360px, calc(100dvh - 250px), 720px)",
            },
          }}
        >
          {/* Search + status remain steady while the desktop list scrolls. */}
          <Box
            sx={{
              p: 1.5,
              borderBottom: `1px solid ${borderColor}`,
              flexShrink: 0,
              backgroundColor: "#FFFFFF",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row", lg: "column", xl: "row" }}
              spacing={1}
            >
              <TextField
                fullWidth
                size="small"
                value={searchTerm}
                placeholder="Search invoice or notes..."
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon
                          sx={{ fontSize: 20, color: mutedText }}
                        />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <FormControl
                size="small"
                sx={{ minWidth: { xs: "100%", sm: 150, lg: "100%", xl: 145 } }}
              >
                <Select<OrderStatusFilter>
                  value={statusFilter}
                  onChange={handleStatusChange}
                  displayEmpty
                >
                  <MenuItem value="ALL">All Status</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="APPROVED">Prepared</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Box>

          {/* Desktop: independent vertical scroll. Mobile: normal page flow. */}
          <Box
            sx={{
              flex: { lg: 1 },
              minHeight: 0,
              overflowY: { xs: "visible", lg: "auto" },
              overscrollBehavior: { lg: "contain" },
              backgroundColor: "#FFFFFF",
            }}
          >
            {loading ? (
              <Box
                sx={{
                  minHeight: 300,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress
                  size={28}
                  sx={{ color: brandColors.header }}
                />
              </Box>
            ) : visibleOrders.length === 0 ? (
              <Box sx={{ py: 8, px: 2, textAlign: "center" }}>
                <ReceiptLongOutlinedIcon
                  sx={{ fontSize: 40, color: "#A7B0AA", mb: 1 }}
                />
                <Typography sx={{ fontWeight: 600, color: "#354239" }}>
                  No orders found
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, color: mutedText }}>
                  Try changing the status or search.
                </Typography>
              </Box>
            ) : (
              visibleOrders.map((order) => {
                const selected = order.invoice_no === selectedInvoiceNo;

                return (
                  <Box
                    key={order.invoice_no}
                    sx={{ borderBottom: `1px solid ${borderColor}` }}
                  >
                    <ButtonBase
                      onClick={() => {
                        setSelectedInvoiceNo(order.invoice_no);
                      }}
                      sx={{
                        width: "100%",
                        display: "block",
                        textAlign: "left",
                        borderLeft: selected
                          ? `3px solid ${brandColors.header}`
                          : "3px solid transparent",
                        backgroundColor: selected ? lightGreen : "#FFFFFF",
                        transition:
                          "background-color 150ms ease, border-color 150ms ease",
                        "&:hover": {
                          backgroundColor: selected ? lightGreen : "#FAFCFA",
                        },
                      }}
                    >
                      <Box sx={{ px: 1.75, py: 1.4 }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, color: "#1B241D" }}
                            >
                              {order.invoice_no}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: mutedText,
                                display: "block",
                                mt: 0.25,
                              }}
                            >
                              {formatDate(order.created_at)} {" • "}
                              {formatTime(order.created_at)}
                            </Typography>
                          </Box>
                          <Stack
                            direction="row"
                            spacing={0.75}
                            sx={{ alignItems: "center", flexShrink: 0 }}
                          >
                            <StatusChip status={order.status} />
                            <ChevronRightRoundedIcon
                              sx={{
                                fontSize: 20,
                                color: selected
                                  ? brandColors.header
                                  : "#718077",
                              }}
                            />
                          </Stack>
                        </Stack>
                        {order.notes && (
                          <Stack
                            direction="row"
                            spacing={0.5}
                            sx={{ alignItems: "center", mt: 0.8, minWidth: 0 }}
                          >
                            <NotesOutlinedIcon
                              sx={{
                                fontSize: 15,
                                color: mutedText,
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              variant="caption"
                              noWrap
                              sx={{ color: mutedText, minWidth: 0 }}
                            >
                              {order.notes}
                            </Typography>
                          </Stack>
                        )}
                      </Box>
                    </ButtonBase>
                  </Box>
                );
              })
            )}
          </Box>

          {/* Pagination remains steady below the desktop scroll area. */}
          {!loading && pagination.totalPages > 0 && (
            <Box
              sx={{
                px: 1.5,
                py: 1.4,
                borderTop: `1px solid ${borderColor}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                flexShrink: 0,
                backgroundColor: "#FFFFFF",
              }}
            >
              <Typography variant="caption" sx={{ color: mutedText }}>
                Page {pagination.page} of {pagination.totalPages}
              </Typography>
              <Pagination
                count={pagination.totalPages}
                page={page}
                onChange={handlePageChange}
                size="small"
                siblingCount={0}
                boundaryCount={1}
                sx={{
                  "& .MuiPaginationItem-root.Mui-selected": {
                    backgroundColor: brandColors.header,
                    color: "#FFFFFF",
                    "&:hover": { backgroundColor: brandColors.header },
                  },
                }}
              />
            </Box>
          )}
        </Paper>

        {/* RIGHT: desktop-only selected invoice detail panel. */}
        <Paper
          elevation={0}
          sx={{
            display: { xs: "none", lg: "flex" },
            flexDirection: "column",
            border: `1px solid ${borderColor}`,
            borderRadius: 2,
            minWidth: 0,
            overflow: "hidden",
            height: "clamp(360px, calc(100dvh - 250px), 720px)",
          }}
        >
          {loading && !selectedOrder ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={30} sx={{ color: brandColors.header }} />
            </Box>
          ) : !selectedOrder ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                px: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <Inventory2OutlinedIcon
                sx={{ fontSize: 48, color: "#A7B0AA", mb: 1.5 }}
              />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Select an order
              </Typography>
              <Typography variant="body2" sx={{ color: mutedText, mt: 0.5 }}>
                Choose an invoice to view its products and details.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                p: 2,
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Paper
                variant="outlined"
                sx={{
                  borderColor,
                  borderRadius: 2,
                  overflow: "hidden",
                  boxShadow: "none",
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Fixed section header. Only the table body scrolls. */}
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: `1px solid ${borderColor}`,
                    flexShrink: 0,
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  <Stack
                    direction="row"
                    sx={{
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Products
                    </Typography>
                    <Typography variant="body2" sx={{ color: mutedText }}>
                      {selectedOrder.transfers.length}{" "}
                      {selectedOrder.transfers.length === 1 ? "item" : "items"}
                    </Typography>
                  </Stack>
                </Box>

                <TableContainer
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: "auto",
                    overscrollBehavior: "contain",
                  }}
                >
                  <Table stickyHeader size="small" sx={{ minWidth: 980 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={tableHeadCellSx} width={56}>
                          #
                        </TableCell>
                        <TableCell sx={tableHeadCellSx}>Product</TableCell>
                        <TableCell align="center" sx={tableHeadCellSx}>
                          Pack Size
                        </TableCell>
                        <TableCell align="center" sx={tableHeadCellSx}>
                          Quantity
                        </TableCell>
                        <TableCell align="center" sx={tableHeadCellSx}>
                          Unpacked
                        </TableCell>
                        <TableCell align="center" sx={tableHeadCellSx}>
                          Unit
                        </TableCell>
                        <TableCell align="right" sx={tableHeadCellSx}>
                          Price
                        </TableCell>
                        <TableCell align="right" sx={tableHeadCellSx}>
                          Amount
                        </TableCell>
                        <TableCell align="center" sx={tableHeadCellSx}>
                          Status
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedOrder.transfers.map((product, index) => (
                        <TableRow
                          key={`${product.transfer_id}-${product.product_id}`}
                          hover
                          sx={{ "&:last-child td": { borderBottom: 0 } }}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ color: mutedText }}
                            >
                              {index + 1}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack
                              direction="row"
                              spacing={1.25}
                              sx={{ alignItems: "center" }}
                            >
                              <Box
                                sx={{
                                  width: 38,
                                  height: 38,
                                  borderRadius: 1.25,
                                  backgroundColor: "#F1F5F2",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: brandColors.header,
                                  flexShrink: 0,
                                }}
                              >
                                <Inventory2OutlinedIcon sx={{ fontSize: 19 }} />
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: "#263129" }}
                              >
                                {product.product}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            {product.packed ?? "—"}
                          </TableCell>
                          <TableCell align="center">
                            {product.qty_packed ?? 0}
                          </TableCell>
                          <TableCell align="center">
                            {product.qty_unpacked ?? 0}
                          </TableCell>
                          <TableCell align="center">
                            {product.unit || "—"}
                          </TableCell>
                          <TableCell align="right">
                            {discountPercent > 0 ? (
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    color: mutedText,
                                    textDecoration: "line-through",
                                    lineHeight: 1.1,
                                  }}
                                >
                                  {formatCurrency(safeNumber(product.msrp))}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    color: "#263129",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {formatCurrency(
                                    safeNumber(product.msrp) *
                                      (1 - discountPercent / 100),
                                  )}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: "#263129",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {formatCurrency(safeNumber(product.msrp))}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: "#263129",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatCurrency(
                                safeNumber(product.msrp) *
                                  safeNumber(product.qty_packed) *
                                  (1 - discountPercent / 100),
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <StatusChip status={product.status} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <PricingSummary
                  subtotal={pricing.subtotal}
                  discountPercent={discountPercent}
                  discountAmount={pricing.discountAmount}
                  tax={pricing.tax}
                  total={pricing.total}
                />
              </Paper>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Mobile/tablet invoice detail modal. The header/stats stay fixed while products scroll. */}
      <Dialog
        open={!isDesktop && Boolean(selectedOrder)}
        onClose={() => {
          setSelectedInvoiceNo(null);
        }}
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              m: { xs: 1, sm: 2 },
              width: { xs: "calc(100% - 16px)", sm: "calc(100% - 32px)" },
              maxHeight: {
                xs: "calc(100dvh - 16px)",
                sm: "calc(100dvh - 32px)",
              },
              height: {
                xs: "calc(100dvh - 16px)",
                sm: "min(760px, calc(100dvh - 32px))",
              },
              borderRadius: { xs: 2.5, sm: 3 },
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            },
          },
          backdrop: {
            sx: {
              backgroundColor: "rgba(15, 24, 18, 0.48)",
              backdropFilter: "blur(2px)",
            },
          },
        }}
      >
        {selectedOrder && (
          <>
            <Box
              sx={{
                flexShrink: 0,
                backgroundColor: "#FFFFFF",
                borderBottom: `1px solid ${borderColor}`,
              }}
            >
              <Box sx={{ px: 1.75, pt: 1.5, pb: 1.25 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      sx={{ alignItems: "center", flexWrap: "wrap" }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: "#18221B",
                          lineHeight: 1.2,
                        }}
                      >
                        Invoice #{selectedOrder.invoice_no}
                      </Typography>
                      <StatusChip status={selectedOrder.status} />
                    </Stack>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", color: mutedText, mt: 0.4 }}
                    >
                      {formatDate(selectedOrder.created_at)} {" • "}
                      {formatTime(selectedOrder.created_at)}
                    </Typography>
                  </Box>

                  <IconButton
                    aria-label="Close invoice details"
                    onClick={() => {
                      setSelectedInvoiceNo(null);
                    }}
                    size="small"
                    sx={{
                      mt: -0.25,
                      mr: -0.5,
                      flexShrink: 0,
                      color: "#526057",
                      backgroundColor: "#F4F7F4",
                      "&:hover": { backgroundColor: "#EBF0EC" },
                    }}
                  >
                    <CloseRoundedIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  borderTop: `1px solid ${borderColor}`,
                  borderBottom: `1px solid ${borderColor}`,
                  backgroundColor: "#FBFCFB",
                }}
              >
                {[
                  { label: "Items", value: selectedOrder.transfers.length },
                  { label: "Quantity", value: totalQuantity },
                  { label: "Unpacked", value: totalUnpackedQuantity },
                ].map((metric, index) => (
                  <Box
                    key={metric.label}
                    sx={{
                      px: 1.25,
                      py: 1.1,
                      textAlign: "center",
                      borderRight:
                        index < 2 ? `1px solid ${borderColor}` : "none",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: mutedText,
                        lineHeight: 1.2,
                      }}
                    >
                      {metric.label}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mt: 0.2,
                        fontWeight: 700,
                        color: "#1D271F",
                        lineHeight: 1.25,
                      }}
                    >
                      {metric.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Stack
                direction="row"
                spacing={1}
                sx={{ px: 1.5, py: 1.15, alignItems: "center" }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="caption" sx={{ color: mutedText }}>
                    Order notes
                  </Typography>
                  <Typography
                    variant="body2"
                    noWrap
                    title={selectedOrder.notes || ""}
                    sx={{ mt: 0.15, color: "#344138", fontWeight: 500 }}
                  >
                    {selectedOrder.notes?.trim() || "No order notes"}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    printing ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <PrintOutlinedIcon />
                    )
                  }
                  disabled={printing}
                  onClick={() => {
                    void handlePrintInvoice(selectedOrder);
                  }}
                  sx={{
                    flexShrink: 0,
                    minHeight: 38,
                    color: "#263128",
                    borderColor: "#D4DBD5",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  {printing ? "Preparing..." : "Print"}
                </Button>
              </Stack>

              <PricingSummary
                subtotal={pricing.subtotal}
                discountPercent={discountPercent}
                discountAmount={pricing.discountAmount}
                tax={pricing.tax}
                total={pricing.total}
                compact
              />
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overscrollBehavior: "contain",
                backgroundColor: pageBackground,
                p: 1.25,
              }}
            >
              <Stack
                direction="row"
                sx={{
                  position: "sticky",
                  top: -10,
                  zIndex: 1,
                  mx: -1.25,
                  mt: -1.25,
                  mb: 1,
                  px: 1.5,
                  py: 1.15,
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: pageBackground,
                  borderBottom: `1px solid ${borderColor}`,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: "#18221B" }}
                >
                  Products
                </Typography>
                <Typography variant="caption" sx={{ color: mutedText }}>
                  {selectedOrder.transfers.length}
                  {selectedOrder.transfers.length === 1 ? " item" : " items"}
                </Typography>
              </Stack>

              <Stack spacing={0.85}>
                {selectedOrder.transfers.map((product, index) => (
                  <Paper
                    key={`${product.transfer_id}-${product.product_id}`}
                    variant="outlined"
                    sx={{
                      p: 1.25,
                      borderColor,
                      borderRadius: 2,
                      boxShadow: "none",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: "#263129",
                            lineHeight: 1.35,
                          }}
                        >
                          {index + 1}. {product.product}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            color: mutedText,
                            mt: 0.4,
                            lineHeight: 1.5,
                          }}
                        >
                          Pack: {product.packed ?? "—"} · Qty:{" "}
                          {product.qty_packed ?? 0}
                          {" · "}Unpacked: {product.qty_unpacked ?? 0} ·{" "}
                          {product.unit || "—"}
                        </Typography>

                        <Stack
                          direction="row"
                          spacing={1.25}
                          sx={{
                            mt: 0.65,
                            alignItems: "baseline",
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: mutedText }}
                          >
                            Price:
                          </Typography>
                          {discountPercent > 0 && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: mutedText,
                                textDecoration: "line-through",
                              }}
                            >
                              {formatCurrency(safeNumber(product.msrp))}
                            </Typography>
                          )}
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: "#263129" }}
                          >
                            {formatCurrency(
                              safeNumber(product.msrp) *
                                (1 - discountPercent / 100),
                            )}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: mutedText }}
                          >
                            Amount:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: brandColors.header }}
                          >
                            {formatCurrency(
                              safeNumber(product.msrp) *
                                safeNumber(product.qty_packed) *
                                (1 - discountPercent / 100),
                            )}
                          </Typography>
                          {normalizeTaxRate(product.hst) > 0 && (
                            <Typography
                              variant="caption"
                              sx={{ color: mutedText }}
                            >
                              +{" "}
                              {formatPercent(
                                normalizeTaxRate(product.hst) * 100,
                              )}
                              % HST
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                      <StatusChip status={product.status} />
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Box>
          </>
        )}
      </Dialog>
    </Box>
  );
};

interface SummaryItemProps {
  label: string;
  value: React.ReactNode;
  hideBorder?: boolean;
}

const SummaryItem: React.FC<SummaryItemProps> = ({
  label,
  value,
  hideBorder = false,
}) => {
  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        borderRight: {
          xs: "none",
          md: hideBorder ? "none" : `1px solid ${borderColor}`,
        },
        borderBottom: {
          xs: `1px solid ${borderColor}`,
          md: "none",
        },
        "&:nth-last-of-type(-n+2)": {
          borderBottom: {
            xs: "none",
            md: "none",
          },
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: mutedText,
          display: "block",
          mb: 0.4,
        }}
      >
        {label}
      </Typography>

      <Typography
        variant="body1"
        sx={{
          fontWeight: 700,
          color: "#1D271F",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const tableHeadCellSx = {
  backgroundColor: "#F8FAF8",
  color: "#59645C",
  fontWeight: 700,
  fontSize: "0.75rem",
  borderBottom: `1px solid ${borderColor}`,
};

export default ClientOrderRequests;
